/**
 * History Transform Functions
 * Transforms API history responses to frontend models
 */

import type { PartDetailsHistoryAPI } from "../api/types";
import type { HistoryEntry, HistoryAction } from "../../../../shared/models";

// ============================================
// Helper Functions
// ============================================

/**
 * Determine the action type from API row flags
 *
 * Business logic:
 * - IsReset=true -> "Repaired"
 * - IsDefaultArray=true & PrevParentPartId=null -> "Created" (first task in spares)
 * - IsDefaultArray=true & PrevParentPartId exists -> "Uninstalled" (removed from roof)
 * - Otherwise -> "Installed" (placed on roof)
 */
const determineAction = (row: PartDetailsHistoryAPI): HistoryAction => {
  if (row.IsReset) {
    return "Repaired";
  }

  if (row.IsDefaultArray) {
    // Check if this is the first task (no previous location) - means part was created
    return row.PrevParentPartId === null ? "Created" : "Uninstalled";
  }

  return "Installed";
};

/**
 * Build a location display string from parent and position names
 *
 * Format: "ParentName - PositionName" or just the available name
 */
const buildLocationDisplay = (
  parentName: string | null,
  positionName: string | null,
): string | null => {
  if (parentName && positionName) {
    return `${parentName} - ${positionName}`;
  }
  return parentName || positionName || null;
};

/**
 * Get the appropriate location name based on action type
 *
 * - For Uninstall: shows where it was uninstalled FROM (previous location)
 * - For Install/Repair: shows where it was installed TO (current location)
 */
const getLocationName = (
  row: PartDetailsHistoryAPI,
  action: HistoryAction,
): string | null => {
  if (action === "Uninstalled" && row.PrevParentPartName) {
    // Show the "from" location for uninstalls
    return buildLocationDisplay(row.PrevParentPartName, row.PrevPositionName);
  }

  // Show the "to" location for installs/repairs
  return buildLocationDisplay(row.ParentPartName, row.PositionName);
};

/**
 * Combine part info fields into a single display string
 *
 * Format: "SerialNumber | Make | Model | Vendor" (filters out nulls/empty)
 */
const combinePartInfo = (row: PartDetailsHistoryAPI): string | null => {
  const parts = [row.SerialNumber, row.Make, row.Model, row.Vendor].filter(
    (v) => v !== null && v !== "",
  );

  return parts.length > 0 ? parts.join(" | ") : null;
};

// ============================================
// Main Transform Function
// ============================================

/**
 * Transform API response from GetPartDetails history to frontend HistoryEntry[]
 *
 * Used for Equipment (Roof) history and Spare Part history.
 * Handles all history modes (Roof, Location, Part).
 *
 * @param apiData - Raw history rows from EQP.GetPartDetails
 * @returns Formatted history entries for UI display
 */
export const transformHistoryAPIToEntries = (
  apiData: PartDetailsHistoryAPI[],
): HistoryEntry[] => {
  return apiData.map((row) => {
    const action = determineAction(row);
    const locationName = getLocationName(row, action);
    const partInfo = combinePartInfo(row);

    return {
      HistoryId: row.TaskId,
      // Handle both string (raw API) and Date (transformed by repository), or null
      Date: row.Timestamp
        ? row.Timestamp instanceof Date
          ? row.Timestamp
          : new Date(row.Timestamp)
        : null,
      Action: action,
      LocationName: locationName,
      Heats: row.Heats ?? 0,
      User: row.User,
      Notes: row.Notes,
      PartInfo: partInfo,
      PartId: row.PartId ?? null,
      IsReset: row.IsReset ?? false,
      IsDefaultArray: row.IsDefaultArray ?? false,
      PreviousTask: row.PreviousTask ?? null,
    };
  });
};
